import React, { useEffect, useState } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useNavigate, useLocation } from "react-router-dom";
import { loginRequest } from "@/authentication/authConfig";
import { InteractionStatus } from "@azure/msal-browser";
import {
  Stack,
  Text,
  IconButton,
  Panel,
  PanelType,
  Spinner,
  SpinnerSize,
} from "@fluentui/react";

/* ------------------------------------------------------------------ */
/*  Menu data                                                         */
/* ------------------------------------------------------------------ */

interface MenuItem {
  path: string;
  icon: string;
  label: string;
  matchPath?: string;
}

interface MenuGroup {
  key: string;
  label: string;
  pathPrefixes: string[];
  items: MenuItem[];
}

const menuGroups: MenuGroup[] = [
  {
    key: "auth",
    label: "Auth",
    pathPrefixes: ["/dashboard", "/customers", "/applications", "/users", "/audits"],
    items: [
      { path: "/dashboard", icon: "ViewDashboard", label: "Dashboard" },
      { path: "/customers", icon: "CityNext", label: "Customers" },
      { path: "/applications", icon: "Product", label: "Applications" },
      { path: "/users", icon: "Contact", label: "Users" },
      { path: "/audits", icon: "ComplianceAudit", label: "Audits" },
    ],
  },
  {
    key: "files",
    label: "Files",
    pathPrefixes: ["/files"],
    items: [{ path: "/files", icon: "FabricFolder", label: "Files" }],
  },
  {
    key: "integrations",
    label: "Integrations",
    pathPrefixes: ["/integrations"],
    items: [
      { path: "/integrations/dashboard", icon: "ViewDashboard", label: "Dashboard", matchPath: "/integrations/dashboard" },
      { path: "/integrations/catalog", icon: "ProductCatalog", label: "Catalog", matchPath: "/integrations/catalog" },
      { path: "/integrations/configurations", icon: "Settings", label: "Configurations", matchPath: "/integrations/configurations" },
      { path: "/integrations/pricing", icon: "Money", label: "Pricing", matchPath: "/integrations/pricing" },
      { path: "/integrations/usage", icon: "BarChart4", label: "Usage", matchPath: "/integrations/usage" },
      { path: "/integrations/customer-defaults", icon: "ContactList", label: "Customer Defaults", matchPath: "/integrations/customer-defaults" },
      { path: "/integrations/feedback", icon: "Feedback", label: "Feedback", matchPath: "/integrations/feedback" },
      { path: "/integrations/audits", icon: "ComplianceAudit", label: "Audits", matchPath: "/integrations/audits" },
    ],
  },
  {
    key: "jobEngine",
    label: "Job Engine",
    pathPrefixes: ["/job-engine"],
    items: [
      { path: "/job-engine",              icon: "ViewDashboard",  label: "Dashboard",    matchPath: "/job-engine" },
      { path: "/job-engine/jobs",         icon: "TaskLogo",       label: "Jobs" },
      { path: "/job-engine/recurring",    icon: "Clock",          label: "Recurring" },
      { path: "/job-engine/dead-letters", icon: "ErrorBadge",     label: "Dead Letters" },
      { path: "/job-engine/workbench",    icon: "DeveloperTools", label: "Workbench" },
    ],
  },
  {
    key: "webhooks",
    label: "Webhooks",
    pathPrefixes: ["/webhooks"],
    items: [
      { path: "/webhooks/inbound", icon: "CloudDownload", label: "Inbound" },
      { path: "/webhooks/outbound", icon: "CloudUpload", label: "Outbound" },
    ],
  },
  {
    key: "platformOps",
    label: "Platform Ops",
    pathPrefixes: ["/cortex-cloning", "/password-reset", "/rename-company"],  // includes /*/audits via startsWith
    items: [
      { path: "/cortex-cloning", icon: "DatabaseSync", label: "Cortex Cloning" },
      { path: "/password-reset", icon: "PasswordField", label: "Password Resets" },
      { path: "/rename-company", icon: "Rename", label: "Rename Company" },
    ],
  },
];

const standaloneItems: MenuItem[] = [
  { path: "/caching", icon: "Database", label: "Caching" },
  { path: "/telemetry/search", icon: "AnalyticsView", label: "API Usage", matchPath: "/telemetry" },
];

/* ------------------------------------------------------------------ */
/*  Reusable nav components                                           */
/* ------------------------------------------------------------------ */

const Separator = () => (
  <div style={{ height: 1, backgroundColor: "#e1e5e9", margin: "8px 0" }} />
);

const navItemStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  height: 40,
  padding: "0 12px",
  cursor: "pointer",
  borderRadius: 4,
  fontSize: 14,
  fontWeight: 400,
  color: "#323130",
  transition: "background-color 0.2s ease",
};

const NavItem = ({
  path,
  icon,
  label,
  matchPath,
  onClick,
}: MenuItem & { onClick?: () => void }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const match = matchPath ?? path;
  const isActive =
    match === "/"
      ? location.pathname === "/"
      : matchPath
        ? location.pathname === matchPath
        : location.pathname.startsWith(match);

  return (
    <div
      onClick={() => {
        navigate(path);
        onClick?.();
      }}
      style={{
        ...navItemStyle,
        backgroundColor: isActive ? "#e3f2fd" : "transparent",
      }}
      onMouseEnter={(e) => {
        if (!isActive) e.currentTarget.style.backgroundColor = "#f0f0f0";
      }}
      onMouseLeave={(e) => {
        if (!isActive) e.currentTarget.style.backgroundColor = "transparent";
      }}
    >
      <i
        className={`ms-Icon ms-Icon--${icon}`}
        style={{ marginRight: 8, fontSize: 16 }}
      />
      {label}
    </div>
  );
};

const NavGroup = ({
  label,
  isExpanded,
  onToggle,
  children,
}: {
  label: string;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) => (
  <>
    <div
      onClick={onToggle}
      style={{
        ...navItemStyle,
        justifyContent: "space-between",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = "#f0f0f0";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "transparent";
      }}
    >
      <span>{label}</span>
      <i
        className={`ms-Icon ms-Icon--${isExpanded ? "ChevronDown" : "ChevronRight"}`}
        style={{ fontSize: 12 }}
      />
    </div>
    {isExpanded && <div style={{ marginLeft: 12 }}>{children}</div>}
  </>
);

/* ------------------------------------------------------------------ */
/*  Layout                                                            */
/* ------------------------------------------------------------------ */

const Layout = (props: any) => {
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState<string | null>("auth");

  const challengeAuthentication = () => {
    try {
      instance.loginRedirect(loginRequest);
    } catch (error) {
      console.log(error);
    }
  };

  const handleLogout = () => {
    instance.logoutRedirect().catch((error) => {
      // Error handling would go here in real app
    });
  };

  useEffect(() => {
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      challengeAuthentication();
    }
  }, [isAuthenticated, inProgress, instance]);

  // Sync menu state with current location on load/refresh
  useEffect(() => {
    const pathname = location.pathname;
    const matched = menuGroups.find((g) =>
      g.pathPrefixes.some((prefix) => pathname.startsWith(prefix)),
    );
    setExpandedGroup(matched ? matched.key : null);
  }, [location.pathname]);

  const toggleGroup = (key: string) =>
    setExpandedGroup((prev) => (prev === key ? null : key));

  const renderMenu = (onNavigate?: () => void) => (
    <div style={{ padding: "0 12px" }}>
      {menuGroups.map((group) => (
        <React.Fragment key={group.key}>
          <NavGroup
            label={group.label}
            isExpanded={expandedGroup === group.key}
            onToggle={() => toggleGroup(group.key)}
          >
            {group.items.map((item) => (
              <NavItem key={item.path} {...item} onClick={onNavigate} />
            ))}
          </NavGroup>
          <Separator />
        </React.Fragment>
      ))}
      {standaloneItems.map((item) => (
        <React.Fragment key={item.path}>
          <NavItem {...item} onClick={onNavigate} />
          <Separator />
        </React.Fragment>
      ))}
    </div>
  );

  if (!isAuthenticated) {
    return <>{props.children}</>;
  }

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Top Navigation Bar */}
      <Stack
        horizontal
        verticalAlign="center"
        styles={{
          root: {
            height: 60,
            padding: "0 24px",
            backgroundColor: "#0078d4",
            color: "white",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            position: "relative",
            zIndex: 1000,
          },
        }}
      >
        {/* Mobile menu button */}
        <IconButton
          iconProps={{ iconName: "GlobalNavButton" }}
          onClick={() => setIsMobileMenuOpen(true)}
          styles={{
            root: {
              color: "white",
              display: "none",
              "@media (max-width: 768px)": {
                display: "block",
              },
            },
          }}
        />

        {/* App Name */}
        <Text
          variant="xLarge"
          styles={{
            root: {
              fontWeight: "600",
              color: "white",
              marginLeft: 8,
            },
          }}
        >
          PCS ADMIN
        </Text>

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Logout Button */}
        <IconButton
          iconProps={{ iconName: "SignOut" }}
          title="Logout"
          onClick={handleLogout}
          styles={{
            root: {
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.1)",
              },
            },
          }}
        />
      </Stack>

      {/* Main Content Area */}
      <Stack horizontal styles={{ root: { flex: 1, overflow: "hidden" } }}>
        {/* Left Sidebar - Desktop */}
        <div
          style={{
            width: 250,
            backgroundColor: "#f8f9fa",
            borderRight: "1px solid #e1e5e9",
            display: "block",
            padding: "16px 0 16px 16px",
          }}
          className="desktop-sidebar"
        >
          {renderMenu()}
        </div>

        {/* Mobile Sidebar Panel */}
        <Panel
          isOpen={isMobileMenuOpen}
          type={PanelType.smallFixedNear}
          onDismiss={() => setIsMobileMenuOpen(false)}
          headerText="Menu"
          closeButtonAriaLabel="Close"
        >
          {renderMenu(() => setIsMobileMenuOpen(false))}
        </Panel>

        {/* Main Content */}
        <div
          style={{
            flex: 1,
            overflow: "visible",
            backgroundColor: "white",
            padding: "0px 24px",
            minWidth: 0,
          }}
        >
          {props.children}
        </div>
      </Stack>

      {/* Full-Screen Loading Overlay */}
      {isLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "24px",
              borderRadius: "8px",
              boxShadow: "0 4px 16px rgba(0, 0, 0, 0.3)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <Spinner size={SpinnerSize.large} />
            <Text variant="medium" styles={{ root: { color: "#323130" } }}>
              Clearing cache...
            </Text>
          </div>
        </div>
      )}

      {/* Responsive styles */}
      <style>
        {`
          @media (max-width: 768px) {
            .desktop-sidebar {
              display: none !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default Layout;
